import { openDB, type IDBPDatabase } from "idb";
import type { RocketDesign } from "../parts/Rocket";

interface Stores {
  rockets: { key: string; value: RocketDesign };
  kv: { key: string; value: unknown };
}

const DB_NAME = "cosmokids";
const DB_VERSION = 1;

export class SaveManager {
  private dbp: Promise<IDBPDatabase<Stores>>;
  private cache = new Map<string, unknown>();

  constructor() {
    this.dbp = openDB<Stores>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        db.createObjectStore("rockets", { keyPath: "id" });
        db.createObjectStore("kv");
      },
    });
  }

  async ready() {
    await this.dbp;
  }

  async listRockets(): Promise<RocketDesign[]> {
    const db = await this.dbp;
    return db.getAll("rockets");
  }

  async getRocket(id: string): Promise<RocketDesign | undefined> {
    const db = await this.dbp;
    return db.get("rockets", id);
  }

  async saveRocket(design: RocketDesign) {
    const db = await this.dbp;
    design.updatedAt = Date.now();
    await db.put("rockets", design);
  }

  async deleteRocket(id: string) {
    const db = await this.dbp;
    await db.delete("rockets", id);
  }

  async kvGet<T>(key: string, fallback: T): Promise<T> {
    if (this.cache.has(key)) return this.cache.get(key) as T;
    const db = await this.dbp;
    const v = (await db.get("kv", key)) as T | undefined;
    const out = v ?? fallback;
    this.cache.set(key, out);
    return out;
  }

  async kvSet<T>(key: string, value: T) {
    this.cache.set(key, value);
    const db = await this.dbp;
    await db.put("kv", value as unknown as never, key);
  }
}
